import { CoverageOverviewResponse } from "./types";

/**
 * A single coverage note section. Notes are addressed by
 * (coverageId, benefitPlanType), so two plans that share a coverage id
 * (e.g. Dental PPO + Dental HMO) map to distinct sections via `noteKey`.
 */
export interface CoverageNoteSection {
  /** Stable note identity: `${coverageId}:${benefitPlanType ?? ""}`. */
  noteKey: string;
  /** Parent coverage id, shared across plans of the same coverage type. */
  coverageId: string;
  /** Plan/product type; undefined for single-plan coverages. */
  benefitPlanType?: string;
  /** Plan-formatted label, e.g. "Dental PPO". */
  label: string;
}

/**
 * Derives per-plan coverage note sections from a coverage summary.
 *
 * The coverage-summary endpoint has one row per plan, each carrying the parent
 * coverage id, plan type, and plan-formatted name (e.g. "Dental PPO"). That is
 * everything a note section needs, so both the RFP notes drawer and the Send
 * RFP review flow build their sections from here — which is what renders
 * Dental PPO and Dental HMO as independent, separately-saved sections.
 *
 * Pass `includeNoteKeys` to restrict output to specific plans (e.g. the ones
 * selected for submission); omit it to include every plan.
 */
export const buildCoverageNoteSections = (
  summary: CoverageOverviewResponse | undefined,
  includeNoteKeys?: ReadonlySet<string>,
): CoverageNoteSection[] => {
  if (!summary) return [];

  const rows = [
    ...(summary.existingCoverages ?? []),
    ...(summary.newCoverages ?? []),
  ];
  const byKey = new Map<string, CoverageNoteSection>();

  for (const row of rows) {
    // Single-plan coverages have no meaningful plan type, so their
    // note lives under the coverage id alone (empty benefitPlanType).
    const benefitPlanType =
      row.productType && row.productType !== "notApplicable"
        ? row.productType
        : undefined;
    const noteKey = benefitPlanType ? `${row.id}:${benefitPlanType}` : row.id;

    if (includeNoteKeys && !includeNoteKeys.has(noteKey)) continue;

    if (!byKey.has(noteKey)) {
      byKey.set(noteKey, {
        noteKey,
        coverageId: row.id,
        benefitPlanType,
        label: row.name,
      });
    }
  }

  return [...byKey.values()];
};
